import React, { useState, useEffect, useMemo } from 'react';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import { Button, CodeDisplay, Loading, useToast } from '@aidkitorg/component-library';
import { NavigationNode } from "@aidkitorg/types/lib/translation/permissions";
import { usePost } from '../API';
import { useParams } from 'react-router-dom';
import { safeParse } from '../Util';
import { v4 } from 'uuid';
import type { Organization } from '@aidkitorg/types/lib/survey';

type MetadataItem = { id: string, key: string; value: string };

// Custom hook to manage ordered, editable metadata entries
export function useOrderedMetadata(initialMetadata: Record<string, any> | undefined) {
  // Store metadata items in an array to keep them in the same order when a key is edited
  const [metadataList, setMetadataList] = useState<MetadataItem[]>([]);

  useEffect(() => {
    if (!initialMetadata) return;
    setMetadataList(
      Object.entries(initialMetadata).map(([key, value]) => ({
        id: v4(), // unique id for stable rendering
        key,
        value,
      }))
    );
  }, [initialMetadata]);

  const updateKey = (id: string, newKey: string) => {
    setMetadataList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, key: newKey } : item
      )
    );
  };

  const updateValue = (id: string, newValue: string) => {
    setMetadataList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, value: newValue } : item
      )
    );
  };

  const addField = () => {
    setMetadataList((prev) => [
      ...prev,
      { id: v4(), key: '', value: '' },
    ]);
  };

  const removeField = (id: string) => {
    setMetadataList((prev) => prev.filter((item) => item.id !== id));
  };

  // Convert metadata list to an object
  const getMetadataObject = (): Record<string, string> => {
    return metadataList.reduce((acc, { key, value }) => {
      if (key !== '') acc[key] = value;
      return acc;
    }, {} as Record<string, string>);
  };

  // Stringify the metadata object with indentation for preview
  const getFormattedMetadata = (): string => {
    return JSON.stringify(getMetadataObject(), null, 2);
  };

  return {
    metadataList,
    updateKey,
    updateValue,
    addField,
    removeField,
    getMetadataObject,
    getFormattedMetadata,
  };
}

function EditOrganization() {
  const [savedOrg, setSavedOrg] = useState<Organization>({ name: '' } as Organization);
  const [name, setName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { id } = useParams() as Record<string, string>
  const { toast } = useToast();
  
  const getOrganization = usePost('/organization/get');
  const updateOrganization = usePost('/organization/update');
  
  useAsyncEffect(async () => {
    setIsLoading(true);
    const org = await getOrganization({ orgId: id });

    if (org?.result) {
      // make sure metadata is an object
      if (typeof org.result.metadata === 'string') {
        org.result.metadata = safeParse(org.result.metadata);
      }
      setSavedOrg(org.result);
      setName(org.result.name);
    // don't show an error message if a logged out user attempts to access this page
    } else if (!org || (org.error && org.error !== 'Route has not been exposed')) {
      toast({
        variant: 'error',
        title: 'Failed to load organization',
      });
    }
    setIsLoading(false);
  }, [id]);

  const {
    metadataList,
    updateKey,
    updateValue,
    addField,
    removeField,
    getMetadataObject,
    getFormattedMetadata,
  } = useOrderedMetadata(savedOrg.metadata);

  const currentMetadata = useMemo(() => getMetadataObject(), [metadataList]);
  const formattedMetadata = useMemo(() => getFormattedMetadata(), [metadataList]);

  const hasNameChanged = name.trim() !== savedOrg.name.trim();
  const hasMetadataChanged = useMemo(
    () => JSON.stringify(savedOrg.metadata) !== JSON.stringify(currentMetadata),
    [savedOrg.metadata, currentMetadata]
  );

  const handleSaveOrganization = async() => {
    setIsSaving(true);

    // initialize with orgUid since it doesn't change and is needed for updates
    const updates: {
      orgId: string;
      name?: string;
      metadata?: string;
    } = { orgId: savedOrg.uid };

    if (hasNameChanged) {
      updates['name'] = name.trim();
    }
    if (hasMetadataChanged) {
      updates['metadata'] = JSON.stringify(currentMetadata);
    }

    const result = await updateOrganization(updates);

    if (result?.success) {
      // Update local "saved" state on succesful save to disable the save button
      const updatedOrg = { ...savedOrg };
      if (updates.name) {
        updatedOrg.name = name;
      }
      if (updates.metadata) {
        updatedOrg.metadata = currentMetadata;
      }
      setSavedOrg(updatedOrg);

      toast({
        variant: 'success',
        title: 'Organization updated successfully',
      });
    } else {
      toast({
        variant: 'error',
        title: 'Failed to update organization',
      });
    }

    setIsSaving(false);
  }
  
  return (
    <Loading loading={isLoading} className="h-full flex flex-col justify-center">
      <div className="pt-5 px-5">
        <h2 className="mb-3">{savedOrg.name}</h2>
        <label htmlFor="organizationName" className="mt-2 mb-1 font-bold">Organization name</label>
        <input
          className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md"
          id="organizationName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Organization name"
        />
        {/* clicking on the label focuses on the first key */}
        <label htmlFor={`metadataKey-${metadataList[0]?.id}`} className="mt-6 mb-1 font-bold">Metadata</label>
        {metadataList.map(item => (
          <div key={item.id} className="flex gap-2">
            <input
              id={`metadataKey-${item.id}`}
              className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md"
              value={item.key}
              onChange={(e) => updateKey(item.id, e.target.value)}
              placeholder="Key"
              aria-label="Metadata key"
            />
            <input
              id={`metadataValue-${item.id}`}
              className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md"
              value={item.value}
              onChange={(e) => updateValue(item.id, e.target.value)}
              placeholder="Value"
              aria-label="Metadata value"
            />
            <button onClick={() => removeField(item.id)} className="text-red-500" aria-label="Remove metadata field">
              ✕
            </button>
          </div>
        ))}
        <Button
          className="border px-3 py-2 mt-1"
          variant="secondary"
          onClick={addField}
        >
          + Add Field
        </Button>

        <h5 className="mt-10">Metadata Preview</h5>
        <CodeDisplay
          language="json"
          code={formattedMetadata}
          className="my-2"
          variant="gray"
          copyable={false}  
          expandable={false}
          showLineNumbers={false}
        />
        <Button
          className="mt-1"
          variant="primary"
          isDisabled={(!hasNameChanged && !hasMetadataChanged) || isSaving}
          loading={isSaving}
          onClick={handleSaveOrganization}
        >
          Save organization
        </Button>
      </div>
    </Loading>
  );
}

export default function OrganizationPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (result?.navigation) {
      setNav(result.navigation);
    }
  }, []);
  
  return <ThreeColumnPage
    nav={nav}
    title="Organization Info"
    main={<EditOrganization />}
  />
}